import { decodeKeyPath, type FilesUrlParts, TOKEN_PARAM } from './files-token';

/**
 * The files worker (flashka-platform `apps/files`) serves the storage bucket
 * on `files.flashka.ai` (dev: `files-dev.flashka.ai`) instead of R2's S3
 * hostname. A files URL is `<origin>/<key>?t=<token>`; the token is in
 * `./files-token`.
 *
 * R2's S3 endpoint, `<account>.r2.cloudflarestorage.com`, resolves to shared
 * Cloudflare IPs that Spanish ISPs block during LaLiga broadcasts, so every
 * presigned URL a client gets stops working in Spain while a match is on. The
 * worker serves the same bucket, under the same keys, from a flashka.ai
 * hostname whose addresses are not the blocked ones. They are still
 * Cloudflare's shared Free-plan addresses, so this holds only while no blocked
 * site shares them. Server-to-server URLs (the transcription provider,
 * Mathpix, CloudConvert) are not blocked and stay on R2.
 *
 * The server hands out files URLs only to a client that sends this header
 * with the value `1`, and only when the deploy has `FILES_ORIGIN` and
 * `FILES_SIGNING_KEY`. flashka-web and the mobile app send it; every other
 * caller (an app build or web bundle from before the switch) keeps getting R2
 * presigned URLs.
 */
export const FILES_URLS_HEADER = 'X-Files-Urls';

/**
 * `{ origin, key, token }` when `url` is a files URL, `null` for anything
 * else (an R2 presigned URL, garbage). The token is opaque here: only the
 * worker checks it.
 */
export function readFilesUrl(
  url: string,
): Pick<FilesUrlParts, 'origin' | 'key' | 'token'> | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const token = parsed.searchParams.get(TOKEN_PARAM);
  // An S3 presigned URL never carries `t`, and always carries X-Amz-*.
  if (!token || parsed.searchParams.has('X-Amz-Signature')) return null;
  const key = decodeKeyPath(parsed.pathname);
  if (!key) return null;
  return { origin: parsed.origin, key, token };
}
